/**
 * Pieces shared by the three roadmap prototypes: the palette, the item chip,
 * the legend, and the local completion state that lets a viewer click items
 * to see how each layout reads as progress accrues.
 */
import {useCallback, useMemo, useState} from 'react';
import {FaBookOpen, FaCheck, FaCode} from 'react-icons/fa';
import styled, {css} from 'styled-components';
import {
  PROTOTYPE_INITIAL_COMPLETED,
  PROTOTYPE_WEEKS,
  type PrototypeItem,
  type PrototypeWeek,
} from './paiRoadmapPrototypeData';

/** Colour of the main line. Matches the PAI course theme's accent. */
export const MAIN_COLOR = '#8c1515';
/** Colour of the going-further track. Matches the splash hero's coral. */
export const FURTHER_COLOR = '#db6b5c';
/** Colour of completed items on either track. */
export const DONE_COLOR = '#3d8f5f';
export const INK = '#111111';
export const MUTED = '#6b6b6b';
export const RULE = '#e6e2dd';
export const SURFACE = '#ffffff';

/** Which of the two tracks an element belongs to. */
export type Track = 'main' | 'further';

/**
 * Local, in-memory completion state for the prototypes.
 *
 * @return The completed item ids, a toggle, and a reset back to the fixture's
 *     starting state.
 */
export function useMockCompletion(): {
  completed: ReadonlySet<string>;
  toggle: (id: string) => void;
  reset: () => void;
} {
  const [completed, setCompleted] = useState<ReadonlySet<string>>(
    () => new Set(PROTOTYPE_INITIAL_COMPLETED),
  );
  const toggle = useCallback((id: string) => {
    setCompleted(previous => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);
  const reset = useCallback(() => {
    setCompleted(new Set(PROTOTYPE_INITIAL_COMPLETED));
  }, []);
  return {completed, toggle, reset};
}

/** Props every prototype layout receives. */
export interface RoadmapPrototypeProps {
  weeks: readonly PrototypeWeek[];
  currentWeekId: string;
  completed: ReadonlySet<string>;
  onToggle: (id: string) => void;
}

/**
 * Whether every item in a list is completed. An empty list counts as done.
 *
 * @param items - The items to check.
 * @param completed - The completed item ids.
 * @return True when no item is outstanding.
 */
export function allDone(
  items: readonly PrototypeItem[],
  completed: ReadonlySet<string>,
): boolean {
  return items.every(item => completed.has(item.id));
}

/**
 * Counts completed items in a list.
 *
 * @param items - The items to count.
 * @param completed - The completed item ids.
 * @return How many of the items are completed.
 */
export function countDone(
  items: readonly PrototypeItem[],
  completed: ReadonlySet<string>,
): number {
  return items.filter(item => completed.has(item.id)).length;
}

/**
 * Totals across every week for one track.
 *
 * @param track - Which track to total.
 * @param completed - The completed item ids.
 * @return Completed and total item counts for the track.
 */
export function useTrackTotals(
  track: Track,
  completed: ReadonlySet<string>,
): {done: number; total: number} {
  return useMemo(() => {
    const items = PROTOTYPE_WEEKS.flatMap(week =>
      track === 'main' ? week.main : week.goingFurther,
    );
    return {done: countDone(items, completed), total: items.length};
  }, [track, completed]);
}

const trackColor = (track: Track) =>
  track === 'main' ? MAIN_COLOR : FURTHER_COLOR;

const ChipButton = styled.button<{
  $track: Track;
  $isDone: boolean;
  $density: 'comfortable' | 'compact';
}>`
  font: inherit;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  text-align: left;
  padding: ${({$density}) =>
    $density === 'compact' ? '0.4rem 0.6rem' : '0.6rem 0.75rem'};
  border-radius: 10px;
  border: 1px solid transparent;
  background: transparent;
  color: ${INK};
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;

  &:hover {
    background: ${({$track}) =>
      $track === 'main'
        ? 'rgba(140, 21, 21, 0.06)'
        : 'rgba(219, 107, 92, 0.1)'};
    border-color: ${({$track}) =>
      $track === 'main'
        ? 'rgba(140, 21, 21, 0.18)'
        : 'rgba(219, 107, 92, 0.35)'};
  }

  ${({$isDone}) =>
    $isDone &&
    css`
      color: ${MUTED};
    `}
`;

const Ring = styled.span<{$track: Track; $isDone: boolean}>`
  flex: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  color: #fff;
  border: 2px ${({$track}) => ($track === 'main' ? 'solid' : 'dashed')}
    ${({$track, $isDone}) => ($isDone ? DONE_COLOR : trackColor($track))};
  background: ${({$isDone}) => ($isDone ? DONE_COLOR : 'transparent')};
  transition:
    background 0.15s,
    border-color 0.15s;
`;

const KindIcon = styled.span<{$track: Track}>`
  flex: none;
  display: inline-flex;
  width: 1.1rem;
  justify-content: center;
  color: ${({$track}) => trackColor($track)};
  opacity: 0.85;
  font-size: 0.85rem;
`;

const ChipText = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.25;
`;

const ChipTitle = styled.span<{$isDone: boolean}>`
  font-weight: 500;
  text-decoration: ${({$isDone}) => ($isDone ? 'line-through' : 'none')};
  text-decoration-color: rgba(0, 0, 0, 0.3);
`;

const ChipBlurb = styled.span`
  font-size: 0.82rem;
  color: ${MUTED};
`;

const KindLabel = styled.span`
  font-size: 0.7rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${MUTED};
  margin-right: 0.35rem;
`;

/**
 * One roadmap item, rendered as a clickable row. Clicking toggles its mock
 * completion.
 */
export function ItemChip({
  item,
  track,
  isDone,
  onToggle,
  showBlurb = false,
  showKindLabel = false,
  density = 'comfortable',
}: {
  item: PrototypeItem;
  track: Track;
  isDone: boolean;
  onToggle: (id: string) => void;
  /** Show the item's one-line hook under its title. Defaults to hidden. */
  showBlurb?: boolean;
  /** Prefix the title with "Learn" / "Code". Defaults to icon only. */
  showKindLabel?: boolean;
  /** Row padding. Defaults to comfortable. */
  density?: 'comfortable' | 'compact';
}) {
  return (
    <ChipButton
      type="button"
      $track={track}
      $isDone={isDone}
      $density={density}
      onClick={() => onToggle(item.id)}
      aria-pressed={isDone}
    >
      <Ring $track={track} $isDone={isDone}>
        {isDone && <FaCheck aria-hidden />}
      </Ring>
      <KindIcon $track={track} aria-hidden>
        {item.kind === 'learn' ? <FaBookOpen /> : <FaCode />}
      </KindIcon>
      <ChipText>
        <ChipTitle $isDone={isDone}>
          {showKindLabel && (
            <KindLabel>{item.kind === 'learn' ? 'Learn' : 'Code'}</KindLabel>
          )}
          {item.title}
        </ChipTitle>
        {showBlurb && item.blurb && <ChipBlurb>{item.blurb}</ChipBlurb>}
      </ChipText>
    </ChipButton>
  );
}

const LegendRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
  font-size: 0.85rem;
  color: ${MUTED};
  align-items: center;
`;

const LegendSwatch = styled.span<{$track: Track}>`
  display: inline-block;
  width: 26px;
  height: 0;
  border-top: 3px ${({$track}) => ($track === 'main' ? 'solid' : 'dashed')}
    ${({$track}) => trackColor($track)};
  margin-right: 0.45rem;
  vertical-align: middle;
`;

/** Explains the two line styles and the two item kinds. */
export function Legend() {
  return (
    <LegendRow>
      <span>
        <LegendSwatch $track="main" />
        The path
      </span>
      <span>
        <LegendSwatch $track="further" />
        Going further (optional)
      </span>
      <span>
        <FaBookOpen aria-hidden style={{marginRight: '0.4rem'}} />
        Learn
      </span>
      <span>
        <FaCode aria-hidden style={{marginRight: '0.4rem'}} />
        Code
      </span>
    </LegendRow>
  );
}

/** Small pill marking the current week. */
export const NowPill = styled.span`
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #fff;
  background: ${MAIN_COLOR};
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
`;

/** Week heading text used across layouts. */
export const WeekTitle = styled.h3`
  font-family: 'Jost', system-ui, sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: ${INK};
`;

/** Secondary week text: date and instructor. */
export const WeekMeta = styled.div`
  font-size: 0.82rem;
  color: ${MUTED};
`;
